import React from 'react';
import { Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import clsx from 'clsx';
import DateInput from 'carbon-react/lib/components/date';
import Textbox from 'carbon-react/lib/components/textbox';
import Button from 'carbon-react/lib/components/button';

import { forceLocalDate } from '@futrli/predict/utils/dates';

import { useConsolidationContext } from '../../../../consolidation-context/consolidation-context';
import styles from './edit-daily-fx-rates-form.scss';

export const EditDailyFxRatesForm = () => {
  const {
    dailyFxRates: { fxRatesOverrideForm, handleAddFxRateOverride, handleDeleteFxRateOverride }
  } = useConsolidationContext();
  const intl = useIntl();

  return (
    <div>
      <ul className={styles.fieldList}>
        {fxRatesOverrideForm.overrideFields.map((field, index) => (
          <li className={styles.fieldListItem} key={field.id} style={{ marginBottom: 20, alignItems: 'center' }}>
            <div
              className={clsx(styles.inputFieldContainerCarbon, styles.currency, {
                [styles.inputFieldCarbonHideLabel]: index !== 0
              })}
              style={{ marginLeft: 0 }}
            >
              <Controller
                name={`overrides.${index}.currencyFrom`}
                control={fxRatesOverrideForm.control}
                defaultValue={field.currencyFrom}
                render={({ field }) => {
                  return (
                    <Textbox
                      {...field}
                      value={field.value}
                      onChange={e => field.onChange(e.target.value)}
                      id="currencyFrom"
                      readOnly
                      data-testid="EditDailyFxRatesForm-currencyFrom"
                      autoComplete="off"
                      size="medium"
                      className={styles.inputField}
                      label={intl.formatMessage({
                        id: 'common.currency',
                        defaultMessage: 'Currency'
                      })}
                      mb={0}
                    />
                  );
                }}
              />
            </div>

            <div
              className={clsx(styles.inputFieldContainerCarbon, {
                [styles.inputFieldCarbonHideLabel]: index !== 0
              })}
            >
              <Controller
                name={`overrides.${index}.dateFrom`}
                control={fxRatesOverrideForm.control}
                defaultValue={field.dateFrom}
                rules={{
                  required: intl.formatMessage({
                    id: 'common.select_date',
                    defaultMessage: 'Please select a date'
                  })
                }}
                render={({ field }) => {
                  return (
                    <div>
                      <DateInput
                        {...field}
                        onChange={e => field.onChange(forceLocalDate(e.target.value.rawValue || ''))}
                        value={field.value ? intl.formatDate(new Date(field.value)) : ''}
                        id="dateFrom"
                        data-testid="EditDailyFxRatesForm-dateFrom"
                        label={intl.formatMessage({
                          id: 'common.from_date',
                          defaultMessage: 'From'
                        })}
                        size="medium"
                        disablePortal={false}
                        maxWidth="180px"
                        error={!!fxRatesOverrideForm.formState.errors?.overrides?.[index]?.dateFrom}
                        mb={0}
                      />
                    </div>
                  );
                }}
              />
            </div>

            <div
              className={clsx(styles.inputFieldContainerCarbon, {
                [styles.inputFieldCarbonHideLabel]: index !== 0
              })}
            >
              <Controller
                name={`overrides.${index}.dateTo`}
                control={fxRatesOverrideForm.control}
                defaultValue={field.dateTo}
                rules={{
                  required: intl.formatMessage({
                    id: 'common.select_date',
                    defaultMessage: 'Please select a date'
                  }),
                  validate: {
                    required: (to: Date) => !!to,
                    greaterThanOrEqualToFromDate: (to: Date) => {
                      const from = fxRatesOverrideForm.getValues(`overrides.${index}.dateFrom`);
                      if (from && to) {
                        return to.getTime() >= from.getTime();
                      }
                      return true;
                    }
                  }
                }}
                render={({ field }) => {
                  return (
                    <div>
                      <DateInput
                        {...field}
                        value={field.value ? intl.formatDate(new Date(field.value)) : ''}
                        id="to"
                        data-testid="EditDailyFxRatesForm-dateTo"
                        label={intl.formatMessage({
                          id: 'common.to_date',
                          defaultMessage: 'To'
                        })}
                        onChange={e => field.onChange(forceLocalDate(e.target.value.rawValue || ''))}
                        disablePortal={false}
                        maxWidth="180px"
                        error={!!fxRatesOverrideForm.formState.errors?.overrides?.[index]?.dateTo}
                        mb={0}
                      />
                    </div>
                  );
                }}
              />
            </div>

            <div
              className={clsx(styles.inputFieldContainerCarbon, {
                [styles.inputFieldCarbonHideLabel]: index !== 0
              })}
            >
              <Controller
                name={`overrides.${index}.dailyRate`}
                control={fxRatesOverrideForm.control}
                defaultValue={field.dailyRate}
                rules={{
                  required: intl.formatMessage({
                    id: 'consolidation.please_enter_a_value',
                    defaultMessage: 'Please enter a value'
                  }),
                  pattern: {
                    value: /^\d+\.?\d*$/,
                    message: intl.formatMessage({
                      id: 'consolidation.please_enter_a_valid_value',
                      defaultMessage: 'Please enter a valid value'
                    })
                  }
                }}
                render={({ field }) => {
                  return (
                    <div style={{ width: '180px' }}>
                      <Textbox
                        {...field}
                        value={field.value}
                        onChange={e => field.onChange(e.target.value)}
                        id="dailyRate"
                        data-testid="EditDailyFxRatesForm-dailyRate"
                        size="medium"
                        error={
                          fxRatesOverrideForm.formState.errors?.overrides?.[index]?.dailyRate
                            ? fxRatesOverrideForm.formState.errors?.overrides?.[index]?.dailyRate?.message
                            : undefined
                        }
                        label={intl.formatMessage({
                          id: 'consolidation.daily_rate',
                          defaultMessage: 'Daily rate'
                        })}
                        placeholder={intl.formatMessage({
                          id: 'consolidation.enter_daily_fx_value',
                          defaultMessage: 'Enter daily FX value'
                        })}
                        maxLength={80}
                        autoComplete="off"
                        mb={0}
                      />
                    </div>
                  );
                }}
              />
            </div>

            <div className={styles.deleteContainer} style={{ marginBottom: 0 }}>
              <Button
                padding="10px"
                buttonType="tertiary"
                onClick={() => handleDeleteFxRateOverride(index)}
                data-testid="EditDailyFxRatesForm-delete"
                aria-label={intl.formatMessage({
                  id: 'billing.delete_daily_rate_for_currency.aria_label',
                  defaultMessage: 'Delete daily rate for currency'
                })}
                iconType="delete"
              />
            </div>
          </li>
        ))}
      </ul>
      <div className={styles.ctaBar}>
        <Button
          onClick={handleAddFxRateOverride}
          data-testid="EditDailyFxRatesForm-add"
          buttonType="secondary"
          iconType="add"
          size="small"
        />
        <div style={{ marginLeft: '8px' }}>
          <FormattedMessage id="consolidation.add_another_fx_modifier" defaultMessage="Add another FX modifier" />
        </div>
      </div>
    </div>
  );
};
